import { useState, useEffect, useCallback } from "react";
import { Bot, ShieldCheck, Loader2, CheckCircle2, XCircle, AlertTriangle, LogIn } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { API_URL } from "../../utils/api";

const BASE = `${API_URL}/api/mcp-oauth`;
const TEAL = "#255E69";
const ELIDO_LOGO = "/elido-logo.png";

export default function ChatGptConsent() {
  const { isAuthenticated, loading: authLoading, user, getAuthHeaders } = useAuth();
  const [rid] = useState(() => new URLSearchParams(window.location.search).get("rid"));
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deciding, setDeciding] = useState(false);

  const authFetch = useCallback(
    (path, opts = {}) =>
      fetch(`${BASE}${path}`, {
        ...opts,
        credentials: "include",
        headers: { "Content-Type": "application/json", ...getAuthHeaders(), ...(opts.headers || {}) },
      }),
    [getAuthHeaders]
  );

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) { setLoading(false); return; }
    if (!rid) { setError("Saknar authorization request (rid)."); setLoading(false); return; }
    (async () => {
      try {
        const r = await authFetch(`/consent/info?rid=${encodeURIComponent(rid)}`);
        if (!r.ok) { setError("Förfrågan hittades inte eller har gått ut. Starta anslutningen på nytt i ChatGPT."); }
        else setInfo(await r.json());
      } catch { setError("Kunde inte hämta förfrågan."); }
      finally { setLoading(false); }
    })();
  }, [authLoading, isAuthenticated, rid, authFetch]);

  const decide = async (approve) => {
    setDeciding(true);
    try {
      const r = await authFetch("/consent/decide", { method: "POST", body: JSON.stringify({ rid, approve }) });
      if (!r.ok) { setError("Beslutet kunde inte registreras."); setDeciding(false); return; }
      const data = await r.json();
      window.location.href = data.redirect_url; // back to ChatGPT
    } catch { setError("Något gick fel."); setDeciding(false); }
  };

  const Shell = ({ children }) => (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <img src={ELIDO_LOGO} alt="Elido" className="h-10 mx-auto mb-4" />
        </div>
        <div className="bg-white rounded-2xl shadow-xl p-8" data-testid="chatgpt-consent-card">{children}</div>
        <p className="text-center text-xs text-gray-400 mt-6">Elido Intelligence · OAuth 2.1 · read-only</p>
      </div>
    </div>
  );

  if (authLoading || loading) {
    return <Shell><div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div></Shell>;
  }

  if (!isAuthenticated) {
    const nextUrl = encodeURIComponent(window.location.pathname + window.location.search);
    return (
      <Shell>
        <div className="text-center" data-testid="consent-login-required">
          <div className="w-14 h-14 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4"><LogIn className="w-7 h-7 text-gray-500" /></div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Logga in som Elido-admin</h2>
          <p className="text-gray-600 text-sm mb-6">Du måste vara inloggad som admin för att godkänna ChatGPT-åtkomst. Förfrågan sparas medan du loggar in.</p>
          <a href={`/login?next=${nextUrl}`} className="inline-flex items-center gap-2 px-4 py-2.5 text-white font-medium rounded-xl" style={{ background: TEAL }} data-testid="consent-login-btn">
            <LogIn className="w-4 h-4" /> Till inloggning
          </a>
          <button onClick={() => window.location.reload()} className="block mx-auto mt-4 text-sm text-gray-500 hover:underline" data-testid="consent-retry-btn">
            Jag har loggat in – försök igen
          </button>
        </div>
      </Shell>
    );
  }

  if (error) {
    return (
      <Shell>
        <div className="text-center" data-testid="consent-error">
          <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4"><AlertTriangle className="w-7 h-7 text-red-600" /></div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Kan inte fortsätta</h2>
          <p className="text-gray-600 text-sm">{error}</p>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <div data-testid="consent-approve-view">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: `${TEAL}15` }}><Bot className="w-6 h-6" style={{ color: TEAL }} /></div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Godkänn åtkomst</h2>
            <p className="text-sm text-gray-500">{info?.app_name} vill ansluta till Elido Intelligence</p>
          </div>
        </div>

        <div className="bg-gray-50 rounded-xl p-4 space-y-2 mb-4 text-sm">
          <Row label="App" value={info?.app_name} />
          <Row label="client_id" value={info?.client_id} mono />
          <Row label="Scope" value={info?.scope} mono />
          <Row label="MCP-resource" value={info?.resource} mono />
          <Row label="Miljö" value={info?.environment} />
          <Row label="Godkänns av" value={user?.email} />
        </div>

        <div className="flex items-start gap-2 text-xs text-gray-500 bg-blue-50 border border-blue-100 rounded-lg p-3 mb-5">
          <ShieldCheck className="w-4 h-4 text-blue-600 mt-0.5 shrink-0" />
          <span>Åtkomsten är strikt <b>read-only</b>. ChatGPT kan läsa Elidos interna data men aldrig ändra eller radera något.</span>
        </div>

        <div className="flex gap-3">
          <button onClick={() => decide(false)} disabled={deciding} data-testid="consent-deny-btn"
            className="flex-1 inline-flex items-center justify-center gap-2 py-3 border border-gray-200 rounded-xl text-gray-700 font-medium hover:bg-gray-50 disabled:opacity-50">
            <XCircle className="w-4 h-4" /> Neka
          </button>
          <button onClick={() => decide(true)} disabled={deciding} data-testid="consent-approve-btn"
            className="flex-1 inline-flex items-center justify-center gap-2 py-3 text-white font-medium rounded-xl disabled:opacity-50" style={{ background: TEAL }}>
            {deciding ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Godkänn
          </button>
        </div>
      </div>
    </Shell>
  );
}

function Row({ label, value, mono }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-gray-500">{label}</span>
      <span className={`text-gray-900 truncate ${mono ? "font-mono text-xs" : ""}`} title={value}>{value || "—"}</span>
    </div>
  );
}
